import lerp from '@sunify/lerp-color';
import imageTypes from 'js/constants/ColorMaps';
import { SATELLITE_LAYERS } from 'js/constants/SatelliteLayers';

const colorStops = imageTypes[SATELLITE_LAYERS.VITALITY_NDVI].intervals;
type Color = { r: number; g: number; b: number };
type Interval = { min: number; max: number };

const toColor = ({ r, g, b }: Color): string => `rgb(${r},${g},${b})`;

const getColor = (value: number): string => {
  if (value <= colorStops[0].min) return toColor(colorStops[0]);

  for (let i = 1; i < colorStops.length; i++) {
    const next = colorStops[i];
    if (value <= next.min) {
      const previous = colorStops[i - 1];
      return lerp(
        toColor(previous),
        toColor(next),
        (value - previous.min) / (next.min - previous.min)
      );
    }
  }

  return toColor(colorStops[colorStops.length - 1]);
};

export default function generateNdviGradient(
  context: CanvasRenderingContext2D | null,
  width: number,
  intervals: Interval[] | null | undefined
): CanvasGradient | null {
  if (!context || !width || !intervals?.length) return null;
  const low = intervals[0].min;
  const high = intervals[intervals.length - 1].max;
  if (!Number.isFinite(low) || !Number.isFinite(high) || high < low) return null;

  // Settings can change the layer before its replacement intervals arrive.
  // Clamp endpoint colors so old seeding/soil values cannot produce null colors.
  const gradient = context.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, getColor(low));
  for (const stop of colorStops) {
    if (stop.min > low && stop.min < high) {
      gradient.addColorStop((stop.min - low) / (high - low), toColor(stop));
    }
  }
  gradient.addColorStop(1, getColor(high));
  return gradient;
}
